// @noflow
import React, { Component } from 'react';
import { isEqual, noop } from 'lodash/fp';

import temperatureColorScaleImage from '~/assets/images/tone-picker-white.png';
import colorScaleImage from '~/assets/images/tone-picker-colour.png';

import ColorPicker from '~/components/widgets/flipper/rgb-flipper';

import { bulbModes } from '~/core/constants/devices';

import { BulbFlipperWrapper } from '../styles';
import { MenuContainer } from '../menu';
import BrightnessPicker from '../standard-brightness-picker';

import DimmerMenu from './dimmer-menu';
import PickerMenu from './picker-menu';

import type { MenuInsertions } from '~/core/types';
import type { Map } from 'immutable';

type Props = {
    activePicker?: string,
    additionalMenuItems?: MenuInsertions,
    bulbBrightness: number,
    bulbColorMode?: string,
    bulbType?: string,
    changeColor?: noop,
    changeColorMode: ({ [string]: string }) => void,
    changePicker?: noop,
    debug?: boolean,
    defaultsForBulbs: Map<string, number>,
    delayRendering?: number,
    handleRadiusPercent?: number,
    isOn: boolean,
    isSaving?: boolean,
    isSmall: boolean,
    isToggling?: boolean,
    isTuneable: boolean,
    canChangeToWhite: boolean,
    logger?: noop,
    pickerDepthPercent?: number,
    productId: string,
    rangeValue?: number,
    rgb?: Uint8ClampedArray,
    toggleLightbulb: noop,
    valueChanged: noop
};

type State = {
    showBrightnessPicker: boolean
};

const offWhiteSample1 = [255, 223, 203];
const offWhiteSample2 = [255, 224, 204];
const isOffWhiteColour = (canChangeToWhite, rgb) => {
    return canChangeToWhite && (isEqual(rgb, offWhiteSample1) || isEqual(rgb, offWhiteSample2));
};

class ColorTuneable extends Component<Props, State> {
    state = {
        showBrightnessPicker: false
    };

    switchComponent(showBrightnessPicker: boolean) {
        this.setState({
            showBrightnessPicker
        });
    }

    switchPicker(showBrightnessPicker?: boolean = false) {
        this.setState({
            showBrightnessPicker
        });
    }

    render() {
        const {
            activePicker,
            additionalMenuItems,
            bulbBrightness,
            bulbColorMode,
            bulbType,
            changeColor,
            changeColorMode,
            changePicker,
            debug,
            defaultsForBulbs,
            delayRendering,
            handleRadiusPercent = 5,
            isOn,
            isSaving,
            isSmall,
            isToggling,
            isTuneable,
            canChangeToWhite,
            logger,
            pickerDepthPercent = 5,
            productId,
            rangeValue,
            rgb,
            toggleLightbulb,
            valueChanged
        } = this.props;

        const { showBrightnessPicker } = this.state;

        const tuneablePickerActive = isTuneable && bulbColorMode === bulbModes.white;
        const isWhite = canChangeToWhite && isOffWhiteColour(canChangeToWhite, rgb);
        const rgbValue = isWhite ? new Uint8ClampedArray([255, 246, 185]) : rgb;

        return (
            <BulbFlipperWrapper>
                {showBrightnessPicker || activePicker === 'brightness' || isWhite ? (
                    <BrightnessPicker
                        isOn={isOn}
                        isSaving={isSaving}
                        isSmall={isSmall}
                        isToggling={isToggling}
                        isVariableColour={!tuneablePickerActive}
                        rgb={rgbValue}
                        toggleLightbulb={toggleLightbulb}
                        value={bulbBrightness}
                        valueChanged={valueChanged}
                    />
                ) : (
                    <ColorPicker
                        bulbBrightness={bulbBrightness}
                        bulbType={bulbType}
                        changeColor={changeColor}
                        colourStartRadians={3.52}
                        colourStopRadians={5.9}
                        debug={debug}
                        defaultsForBulbs={defaultsForBulbs}
                        delayRendering={delayRendering}
                        handleRadiusPercent={handleRadiusPercent}
                        image={tuneablePickerActive ? temperatureColorScaleImage : colorScaleImage}
                        isCustomRange={tuneablePickerActive}
                        isOn={isOn}
                        isSaving={isSaving}
                        isSmall={isSmall}
                        isToggling={isToggling}
                        logger={logger}
                        pickerDepthPercent={pickerDepthPercent}
                        productId={productId}
                        rangeValue={rangeValue}
                        rgb={rgbValue}
                        toggleLightbulb={toggleLightbulb}
                    />
                )}

                <MenuContainer isSmall={isSmall}>
                    {showBrightnessPicker || activePicker === 'brightness' || isWhite ? (
                        <DimmerMenu
                            additionalItems={additionalMenuItems}
                            isOn={isOn}
                            onClick={() => {
                                if (changePicker) {
                                    changePicker({
                                        productId,
                                        pickerType: tuneablePickerActive ? 'white' : 'colour'
                                    });
                                } else {
                                    const showBrightness = isWhite && changeColor ? false : !showBrightnessPicker;
                                    this.switchComponent(showBrightness);
                                }

                                if (isWhite && changeColor) {
                                    changeColor({
                                        hsv: { h: 0, s: 100 },
                                        mode: bulbModes.colour,
                                        productId
                                    });
                                }
                            }}
                            temperaturePickerActive={tuneablePickerActive}
                        />
                    ) : (
                        <PickerMenu
                            additionalItems={additionalMenuItems}
                            canChangeToWhite={canChangeToWhite}
                            isOn={isOn}
                            isTuneable={isTuneable}
                            switchPicker={() => {
                                this.switchPicker();
                                changeColorMode({
                                    mode: tuneablePickerActive ? bulbModes.colour : bulbModes.white,
                                    productId: productId
                                });
                            }}
                            temperaturePickerActive={tuneablePickerActive}
                            toggleDimmer={() => {
                                if (changePicker) {
                                    changePicker({
                                        pickerType: 'brightness',
                                        productId
                                    });
                                } else {
                                    this.switchComponent(!showBrightnessPicker);
                                }
                            }}
                            toggleWhite={() => {
                                if (changeColor) {
                                    changeColor({
                                        hsv: {
                                            h: defaultsForBulbs.get('offWhiteHue'),
                                            s: defaultsForBulbs.get('offWhiteSaturation')
                                        },
                                        mode: bulbModes.white,
                                        pickerType: 'brightness',
                                        productId
                                    });
                                }
                            }}
                        />
                    )}
                </MenuContainer>
            </BulbFlipperWrapper>
        );
    }
}

export default ColorTuneable;



// WEBPACK FOOTER //
// ./src/components/bulbs/color-tuneable/index.js